import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@asm/ui";
import {
  Plus,
  Search,
  Trash2,
  Pause,
  Play,
  ChevronLeft,
  ChevronRight,
  Target,
} from "lucide-react";
import { adminApi, ReconCampaign, PaginatedCampaigns } from "@/lib/api";
import { CampaignForm } from "./CampaignForm";
import { CampaignDetail } from "./CampaignDetail";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  paused: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  completed: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  archived: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
};

function formatSector(sector: string): string {
  if (sector === "unspecified") return "Any sector";
  return sector.charAt(0).toUpperCase() + sector.slice(1);
}

function formatCountry(code: string): string {
  if (code === "XX") return "Multiple";
  return code;
}

interface ReconCampaignsProps {
  reconEnabled?: boolean;
}

export function ReconCampaigns({ reconEnabled = false }: ReconCampaignsProps) {
  const { toast } = useToast();
  const [data, setData] = useState<PaginatedCampaigns | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<ReconCampaign | undefined>();
  const [selectedCampaign, setSelectedCampaign] = useState<ReconCampaign | null>(null);

  const perPage = 20;

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const result = await adminApi.listCampaigns({
        page,
        per_page: perPage,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setData(result);
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to load campaigns",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [page, statusFilter]);

  const handleSearch = () => {
    setPage(1);
    fetchCampaigns();
  };

  const handleToggleStatus = async (campaign: ReconCampaign) => {
    try {
      if (campaign.status === "active") {
        await adminApi.pauseCampaign(campaign.id);
        toast({ title: "Campaign Paused", description: `${campaign.name} has been paused.` });
      } else {
        await adminApi.resumeCampaign(campaign.id);
        toast({ title: "Campaign Resumed", description: `${campaign.name} is now active.` });
      }
      fetchCampaigns();
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to update campaign",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (campaign: ReconCampaign) => {
    if (!confirm(`Delete campaign "${campaign.name}"? This cannot be undone.`)) return;

    try {
      await adminApi.deleteCampaign(campaign.id);
      toast({ title: "Campaign Deleted", description: `${campaign.name} has been deleted.` });
      fetchCampaigns();
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to delete campaign",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (campaign: ReconCampaign) => {
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setEditingCampaign(undefined);
    fetchCampaigns();
  };

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString();
  };

  // If a campaign is selected, show the detail view
  if (selectedCampaign) {
    return (
      <CampaignDetail
        campaign={selectedCampaign}
        onBack={() => {
          setSelectedCampaign(null);
          fetchCampaigns();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Campaigns</CardTitle>
              <CardDescription>
                {data?.total ?? 0} campaign{data?.total !== 1 ? "s" : ""} configured
              </CardDescription>
            </div>
            <Button onClick={() => { setEditingCampaign(undefined); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-3 mb-4">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search campaigns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Domains</TableHead>
                  <TableHead className="text-right">Scanned</TableHead>
                  <TableHead>Last Scan</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : !data?.campaigns.length ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Target className="h-8 w-8 opacity-50" />
                        <p>No campaigns yet</p>
                        <p className="text-xs">Create a campaign to start targeted reconnaissance.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.campaigns.map((campaign) => (
                    <TableRow
                      key={campaign.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedCampaign(campaign)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {reconEnabled && campaign.status === "active" && (
                            <span className="relative flex h-2 w-2 shrink-0">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                            </span>
                          )}
                          <div>
                            <div className="font-medium">{campaign.name}</div>
                            {campaign.description && (
                              <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {campaign.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={campaign.sector === "unspecified" ? "text-muted-foreground italic" : ""}>
                          {formatSector(campaign.sector)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={campaign.country_code === "XX" ? "text-muted-foreground italic" : ""}>
                          {formatCountry(campaign.country_code)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_COLORS[campaign.status] || ""}>
                          {campaign.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {campaign.domain_count.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {campaign.scanned_count.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(campaign.last_scan_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(campaign)}
                            title={campaign.status === "active" ? "Pause" : "Resume"}
                          >
                            {campaign.status === "active" ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(campaign)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(campaign)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({data?.total} total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CampaignForm
        open={showForm}
        onOpenChange={(open) => {
          setShowForm(open);
          if (!open) setEditingCampaign(undefined);
        }}
        campaign={editingCampaign}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
